import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  directHoverSelector,
} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';

/*
 * Props.
 */

interface StaticViewerBannerProps {
  iconName?: IconName;
  icon?: ReactNode;
  color: PaletteColorsEnum;
  className?: string;
  date?: number;
  overflowContent?: React.ReactNode;
  action?: ReactNode;
}

/*
 * Style.
 */

const StyledStaticDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
`;

interface BannerStyleProps {
  $color: PaletteColorsEnum;
}
const StyledBannerDiv = styled.div<BannerStyleProps>`
  display: flex;
  gap: 10px;
  align-items: center;

  margin-top: 16px;
  padding: 9px 12px;
  border-radius: 100px;
  background-color: ${(p) => p.theme.palette[p.$color].shade20};

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.palette[p.$color].shade50};
`;

const StyledContentDiv = styled.div`
  /* Prevents overflow content from growing the banner larger than its container */
  overflow: hidden;
`;

const StyledTimestampWrapperDiv = styled.div`
  text-align: right;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade60};
  position: relative;

  /* Visible on hover. */
  opacity: 0;

  ${directHoverSelector} {
    opacity: 1;
  }
`;

const StyledTimestampSpan = styled.span`
  position: absolute;
  bottom: 10px;
  right: 0;
`;

/*
 * Component.
 */

export const StaticViewerBanner: FC<PropsWithChildren<StaticViewerBannerProps>> = (props) => {
  // Allow for specific content when the children overflows.
  const overflowContent = props.overflowContent || props.children;

  const namedIcon = props.iconName ? <Icon name={props.iconName} /> : null;
  const icon = props.icon || namedIcon;

  return (
    <StyledStaticDiv className={buildHoverParentClassName()}>
      <div />
      <TooltipCoordinator render={() => overflowContent}>
        <StyledBannerDiv className={props.className} $color={props.color}>
          {icon}
          <StyledContentDiv>
            <TooltipOverflowCondition>{props.children}</TooltipOverflowCondition>
          </StyledContentDiv>
          {props.action}
        </StyledBannerDiv>
      </TooltipCoordinator>
      <StyledTimestampWrapperDiv>
        {props.date && (
          <StyledTimestampSpan>
            <InteractiveDateTimeFormatter
              relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
              value={props.date}
            />
          </StyledTimestampSpan>
        )}
      </StyledTimestampWrapperDiv>
    </StyledStaticDiv>
  );
};
